import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../app/store";
import { DEFAULT_SIGN_LANGUAGE, getSignLanguage, SIGN_LANGUAGES, SignLanguageId } from "../../data/signLanguages";

export interface SettingsState {
  signLanguage: SignLanguageId;
}

export const initialSettings: SettingsState = { signLanguage: DEFAULT_SIGN_LANGUAGE };

const isSignLanguage = (id: unknown): id is SignLanguageId =>
  typeof id === "string" && Object.prototype.hasOwnProperty.call(SIGN_LANGUAGES, id);

/**
 * redux-persist migration. Learners from before languages existed were learning ASL:
 * keep them on ASL if they have ASL progress, otherwise start them on the PSL default.
 */
export function migrateSettings<S extends { settings?: Partial<SettingsState>; progress?: { unitProgress?: Record<string, number> } }>(
  state: S | undefined
): S | undefined {
  if (!state) return state;
  const current = state.settings?.signLanguage;
  if (isSignLanguage(current)) return state;
  const units = Object.keys(state.progress?.unitProgress ?? {});
  const hadAsl = current === undefined && units.some((id) => !id.startsWith("psl-"));
  return { ...state, settings: { ...state.settings, signLanguage: hadAsl ? "asl" : DEFAULT_SIGN_LANGUAGE } };
}

const settingsSlice = createSlice({
  name: "settings",
  initialState: initialSettings,
  reducers: {
    setSignLanguage(state, action: PayloadAction<SignLanguageId>) {
      if (isSignLanguage(action.payload)) state.signLanguage = action.payload;
    },
  },
});

export const { setSignLanguage } = settingsSlice.actions;
export default settingsSlice.reducer;

export const selectSignLanguage = (state: RootState) => getSignLanguage(state.settings?.signLanguage);
